<template>
  <div class="toggle-pills" :class="[sizeClass, { 'toggle-pills--block': block }]">
    <button 
      v-for="option in normalizedOptions" 
      :key="option.value"
      :class="['toggle-pills__option', { 'toggle-pills__option--active': value === option.value }]"
      :style="value === option.value ? activeStyle : null"
      :disabled="option.disabled"
      @click="$emit('input', option.value)">
      <slot name="option" :option="option">
        {{ option.label }}
      </slot>
    </button>
  </div>
</template>

<script>
export default {
  name: 'TogglePills',
  
  props: {
    /**
     * The currently selected value (use v-model)
     */
    value: { type: [String, Number, Boolean], default: null },
    
    /**
     * Array of options. Can be:
     * - Array of primitives: [1, 2, 3] or ['a', 'b', 'c']
     * - Array of objects: [{ value: 1, label: 'One' }, { value: 2, label: 'Two' }]
     */
    options: { type: Array, required: true },
    
    /**
     * Accent color for the active pill
     * Defaults to the app's primary color
     */
    accentColor: { type: String, default: '#c25354' },
    
    /**
     * Size variant: 'sm', 'md', 'lg'
     */
    size: { type: String, default: 'md', validator: v => ['sm', 'md', 'lg'].includes(v) },
    
    /**
     * If true, the toggle will take full width of its container
     */
    block: { type: Boolean, default: false }
  },
  
  computed: {
    normalizedOptions() {
      return this.options.map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return { value: opt.value, label: opt.label ?? opt.value, disabled: opt.disabled ?? false };
        }
        return { value: opt, label: String(opt), disabled: false };
      });
    },
    
    activeStyle() {
      return {
        '--pill-active-bg': this.accentColor,
        '--pill-active-color': '#fff'
      };
    },
    
    sizeClass() {
      return `toggle-pills--${this.size}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.toggle-pills {
  display: inline-flex;
  align-self: flex-start;
  background: rgba(128, 128, 128, 0.15);
  border-radius: 20px;
  padding: 3px;
  
  &--block {
    display: flex;
    align-self: stretch;
    width: 100%;
    
    .toggle-pills__option {
      flex: 1;
    }
  }
  
  // Size variants
  &--sm {
    border-radius: 16px;
    padding: 2px;
    
    .toggle-pills__option {
      padding: 4px 10px;
      font-size: 10px;
      border-radius: 14px;
    }
  }
  
  &--md {
    .toggle-pills__option {
      padding: 5px 14px;
      font-size: 11px;
      border-radius: 16px;
    }
  }
  
  &--lg {
    border-radius: 24px;
    padding: 4px;
    
    .toggle-pills__option {
      padding: 8px 20px;
      font-size: 13px;
      border-radius: 20px;
    }
  }
  
  &__option {
    all: unset;
    box-sizing: border-box;
    font-weight: 500;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    text-align: center;
    white-space: nowrap;
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &--active {
      background: var(--pill-active-bg, #6C84FF);
      color: var(--pill-active-color, #fff);
    }
    
    &:hover:not(&--active):not(:disabled) {
      background: rgba(128, 128, 128, 0.1);
    }
  }
}
</style>

